/**
 * SOLOMON — GEO TEXTAREA
 *
 * Freeform multi-line geo identifier input. One identifier per line; comma
 * fallback for bare ZIPs on a single line. Provides live count + format
 * hints. Server-side parser (geoIdentifierParser.parseGeoInput) is the
 * authoritative validator on submit; this component shows lightweight
 * "looks roughly OK / line N needs a state suffix" hints for live UX
 * without duplicating the full state-name lookup table on the client.
 *
 * Props:
 *   value           — controlled text
 *   onChange(text)
 *   onClear()       — invoked when operator clicks Clear (parent clears state)
 *   placeholder?    — text shown when textarea is empty (default: hint text)
 *   parseErrors?    — array of {line, raw, message} from a prior server response
 */

const ZIP_LINE = /^\s*\d{5}\s*$/;
const ZIPS_COMMA = /^\s*\d{5}(\s*,\s*\d{5})+\s*$/;
const STATE_CODE = /^\s*[A-Za-z]{2}\s*$/;
const CITY_OR_COUNTY = /^\s*[A-Za-z][A-Za-z .'-]+,\s*[A-Za-z]/;   // "Something, X" — final validation server-side
const FULL_STATE = /^\s*(alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|new hampshire|new jersey|new mexico|new york|north carolina|north dakota|ohio|oklahoma|oregon|pennsylvania|rhode island|south carolina|south dakota|tennessee|texas|utah|vermont|virginia|washington|west virginia|wisconsin|wyoming|district of columbia)\s*$/i;
const ADDRESS_W_ZIP = /,\s*[A-Za-z]{2}\s+\d{5}\s*$/;

function classifyLine(raw) {
  const line = String(raw).trim();
  if (!line) return { kind: 'blank' };
  if (ZIP_LINE.test(line))       return { kind: 'zip', count: 1 };
  if (ZIPS_COMMA.test(line))     return { kind: 'zip', count: line.split(',').length };
  if (FULL_STATE.test(line))     return { kind: 'state', count: 1 };
  if (STATE_CODE.test(line))     return { kind: 'state', count: 1 };
  if (ADDRESS_W_ZIP.test(line))  return { kind: 'zip', count: 1, note: 'address → trailing ZIP' };
  if (CITY_OR_COUNTY.test(line)) return { kind: 'city_or_county', count: 1 };
  return {
    kind: 'unknown',
    message: 'unrecognized — try "46322", "Indianapolis, IN", "Marion County, IN", or a state code/name',
  };
}

function summarize(text) {
  if (!text) return { ok: 0, errors: [], totalIdentifiers: 0, breakdown: {} };
  const lines = String(text).split(/\r?\n/);
  let ok = 0, totalIdentifiers = 0;
  const errors = [];
  const breakdown = { zip: 0, city_or_county: 0, state: 0 };
  lines.forEach((raw, idx) => {
    const c = classifyLine(raw);
    if (c.kind === 'blank') return;
    if (c.kind === 'unknown') {
      errors.push({ line: idx + 1, raw: raw.trim(), message: c.message });
      return;
    }
    ok += 1;
    totalIdentifiers += c.count;
    breakdown[c.kind] = (breakdown[c.kind] || 0) + c.count;
  });
  return { ok, errors, totalIdentifiers, breakdown };
}

export default function GeoTextarea({ value, onChange, onClear, placeholder, parseErrors }) {
  const summary = summarize(value);
  const hasContent = value && value.trim().length > 0;

  // Server-returned parse errors take precedence (more authoritative) over
  // the live client classification.
  const displayErrors = (Array.isArray(parseErrors) && parseErrors.length > 0)
    ? parseErrors
    : summary.errors;

  return (
    <div className="ls-geo">
      <textarea
        className="ls-geo-textarea"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Enter ZIPs, "City, ST", "County, ST", or state — one per line'}
        rows={4}
        spellCheck={false}
      />
      <div className="ls-geo-toolbar">
        <button
          type="button"
          className="ls-geo-clear"
          onClick={onClear}
          disabled={!hasContent}
          title="Reset the textarea to empty"
        >
          Clear
        </button>
        <div className="ls-geo-status">
          {!hasContent && <span className="ls-geo-hint">no identifiers yet</span>}
          {hasContent && displayErrors.length === 0 && (
            <span className="ls-geo-ok">
              ✓ {summary.totalIdentifiers} identifier{summary.totalIdentifiers === 1 ? '' : 's'}
              {summary.breakdown.zip            ? ` · ${summary.breakdown.zip} zip`            : ''}
              {summary.breakdown.city_or_county ? ` · ${summary.breakdown.city_or_county} city/county` : ''}
              {summary.breakdown.state          ? ` · ${summary.breakdown.state} state`        : ''}
              {' '}· est {summary.ok} BatchData call{summary.ok === 1 ? '' : 's'}
            </span>
          )}
          {hasContent && displayErrors.length > 0 && (
            <span className="ls-geo-warn">
              ⚠ {summary.ok} valid, {displayErrors.length} error{displayErrors.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>
      {displayErrors.length > 0 && (
        <ul className="ls-geo-errors">
          {displayErrors.map((e, i) => (
            <li key={i}>
              <strong>Line {e.line}:</strong> {e.message}
              {e.raw ? <span className="ls-geo-error-raw"> — "{e.raw}"</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Helper for parent: serialize a saved-market's typed identifiers back to
// textarea format. Inverse of geoIdentifierParser.parseGeoInput.
export function serializeIdentifiers(identifiers) {
  if (!Array.isArray(identifiers)) return '';
  return identifiers.map((g) => {
    if (g.type === 'zip')    return g.value;
    if (g.type === 'city')   return `${g.value}, ${g.state}`;
    if (g.type === 'county') return `${g.value}, ${g.state}`;
    if (g.type === 'state')  return g.value;
    return '';
  }).filter(Boolean).join('\n');
}
